import React, { useEffect, useState } from "react";
import MainLayout from "./MainLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  TextField,
  Box,
  Grid,
  InputAdornment,
  TableSortLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import * as XLSX from "xlsx";
import { Search, FileUpload, FileDownload } from "@mui/icons-material";
import { Power } from "lucide-react";
import TableChartIcon from "@mui/icons-material/TableChart";
import { TableBodySkeleton } from "../utils/TableSkeleton";
import Swal from "sweetalert2";
import Apiurl from "../../Helpers/apiUrl";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdminInventory } from "../../redux/slices/productSlice";
import UploadProgressBar from "../utils/UploadProgressBar";
import { ClickableTableLink } from "./ClickableTableLink";

export default function Inventory() {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [inventoryVisible, setInventoryVisible] = useState(false);
  const [toggling, setToggling] = useState(false);
  const { inventory, count, getStatus } = useSelector((state) => state.product);
  const totalCount = count || 0;
  const [sortBy, setSortBy] = useState({
    sort_by: "",
    order: "desc",
  });

  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [pendingStatus, setPendingStatus] = useState("");
  const [pruningDateVal, setPruningDateVal] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        fetchAdminInventory({
          search: searchQuery,
          page: page + 1,
          limit: rowsPerPage,
          sort_by: sortBy.sort_by,
          sort_order: sortBy.order,
        })
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, page, rowsPerPage, sortBy, dispatch]);

  useEffect(() => {
    fetch(`${Apiurl}system-settings`, { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        if (data.status) {
          setInventoryVisible(data.inventory_menu === 1);
        }
      })
      .catch(err => console.error(err));
  }, []);

  const handleToggle = async () => {
    const newState = !inventoryVisible;
    setInventoryVisible(newState);
    setToggling(true);
    try {
      const token = localStorage.getItem("u_token");
      const response = await fetch(`${Apiurl}admin/inventory-settings/toggle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ inventory_menu: newState ? 1 : 0 }),
      });
      const data = await response.json();
      if (!data.status) {
        Swal.fire("Error", "Failed to update setting.", "error");
        setInventoryVisible(!newState);
      } else {
        Swal.fire({
          title: "Success",
          text: "Inventory visibility updated successfully.",
          icon: "success",
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Network error.", "error");
      setInventoryVisible(!newState);
    } finally {
      setToggling(false);
    }
  };

  const formatStockValue = (num) => {
    if (num === 0) return "0";
    if (!num || isNaN(num)) return "-";
    const val = Math.abs(num);
    if (val >= 10000000) return (num / 10000000).toFixed(1) + " Cr";
    if (val >= 100000) return (num / 100000).toFixed(1) + " L";
    if (val >= 1000) return (num / 1000).toFixed(1) + " K";
    return num.toString();
  };

  const getMonthLabels = () => {
    const now = new Date();
    const f = new Intl.DateTimeFormat("en-US", {
      month: "short",
      year: "numeric",
    });
    return {
      currentMonth: f.format(new Date(now.getFullYear(), now.getMonth(), 1)),
      nextMonth: f.format(new Date(now.getFullYear(), now.getMonth() + 1, 1)),
    };
  };

  const { currentMonth, nextMonth } = getMonthLabels();

  const handleDownloadExcel = () => {
    if (!inventory?.length) return;

    const exportData = inventory.map((row, index) => ({
      "SL No.": index + 1,
      "SKU Code": row.sku_code || "—",
      Description: row.product?.product_desc || "—",
      Indicator: row.stockIndicator || "—",
      Available: row.stockAvailable || 0,
      [`${currentMonth} WK1`]: row.cwk_1 || 0,
      [`${currentMonth} WK2`]: row.cwk_2 || 0,
      [`${currentMonth} WK3`]: row.cwk_3 || 0,
      [`${currentMonth} WK4`]: row.cwk_4 || 0,
      [`${nextMonth} WK1`]: row.nwk_1 || 0,
      [`${nextMonth} WK2`]: row.nwk_2 || 0,
      [`${nextMonth} WK3`]: row.nwk_3 || 0,
      [`${nextMonth} WK4`]: row.nwk_4 || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory_Report");

    const wscols = [
      { wch: 8 }, // SL No
      { wch: 15 }, // SKU
      { wch: 40 }, // Description (wider)
      { wch: 10 }, // Indicator
      { wch: 12 }, // Available
    ];
    ws["!cols"] = wscols;

    XLSX.writeFile(
      wb,
      `Inventory_Report_${new Date().toLocaleDateString()}.xlsx`
    );
  };

  const formatExcelDate = (val) => {
    if (!val) return "";

    // If it's already a Date object (parsed by XLSX with cellDates: true)
    if (val instanceof Date && !isNaN(val)) {
      const day = String(val.getDate()).padStart(2, "0");
      const month = String(val.getMonth() + 1).padStart(2, "0");
      const year = val.getFullYear();
      return `${day}-${month}-${year}`;
    }

    // If it's passed as an Excel serial number
    if (typeof val === "number") {
      const parsedDate = XLSX.SSF.parse_date_code(val);
      if (parsedDate) {
        const day = String(parsedDate.d).padStart(2, "0");
        const month = String(parsedDate.m).padStart(2, "0");
        const year = parsedDate.y;
        return `${day}-${month}-${year}`;
      }
    }

    // If it's already a string, return it as-is
    return String(val);
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        sku_code: "26352000",
        stockIndicator: "MTS",
        stockAvailable: 23419,
        status: 'Active',
        pruning_date: '25-08-2026',
        cwk_1: 0,
        cwk_2: 0,
        cwk_3: 0,
        cwk_4: 0,
        nwk_1: 0,
        nwk_2: 0,
        nwk_3: 0,
        nwk_4: 0,
      },
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Stock_Upload_Template.xlsx");
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    setLoading(true);
    setUploadProgress(0);

    reader.onload = async (evt) => {
      try {
        const bstr = evt?.target?.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const importedData = XLSX.utils.sheet_to_json(
          wb.Sheets[wb.SheetNames[0]],
          { defval: "" }
        );

        const commonBatchId = `BATCH-${Date.now()}-${Math.floor(
          Math.random() * 1000
        )}`;

        const validatedData = importedData.map((row) => ({
          batchId: commonBatchId,
          sku_code: String(row.sku_code || ""),
          product_description: row.product_desc || "",
          stockIndicator: row.stockIndicator || "MTS",
          stockAvailable: Number(row.stockAvailable) || 0,
          status: row.status || "",
          pruning_date: formatExcelDate(row.pruning_date),
          cwk_1: Number(row.cwk_1) || 0,
          cwk_2: Number(row.cwk_2) || 0,
          cwk_3: Number(row.cwk_3) || 0,
          cwk_4: Number(row.cwk_4) || 0,
          nwk_1: Number(row.nwk_1) || 0,
          nwk_2: Number(row.nwk_2) || 0,
          nwk_3: Number(row.nwk_3) || 0,
          nwk_4: Number(row.nwk_4) || 0,
        }));

        const chunkSize = 300;
        const totalChunks = Math.ceil(validatedData.length / chunkSize);

        for (let i = 0; i < totalChunks; i++) {
          const chunk = validatedData.slice(i * chunkSize, (i + 1) * chunkSize);
          await axios.post(
            `${Apiurl}admin/inventory/add`,
            { data: chunk },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("u_token")}`,
              },
            }
          );
          setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
        }

        Swal.fire(
          "Success",
          `Upload Complete. Batch ID: ${commonBatchId}`,
          "success"
        );
        dispatch(
          fetchAdminInventory({
            search: searchQuery,
            page: page + 1,
            limit: rowsPerPage,
            sort_by: sortBy.sort_by,
          sort_order: sortBy.order,
          })
        );
      } catch (err) {
        Swal.fire("Error", "Upload failed", "error");
      } finally {
        setLoading(false);
        setUploadProgress(0);
        if (e.target) e.target.value = null;
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      }
      return { sort_by: column, order: "asc" };
    });
  };

  const handleStatusChange = async (row, newStatus, dateVal = "") => {
    if (newStatus === "Pruning" && !dateVal) {
      let defaultDate = new Date().toISOString().split("T")[0];
      if (row.pruning_date && row.pruning_date.includes("-")) {
        const parts = row.pruning_date.split("-");
        if (parts.length === 3) defaultDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }

      setSelectedRow(row);
      setPendingStatus(newStatus);
      setPruningDateVal(defaultDate);
      setDateModalOpen(true);
      return;
    }

    let pruningDate = "";
    if (newStatus === "Pruning" && dateVal) {
      const [y, m, d] = dateVal.split("-");
      pruningDate = `${d}-${m}-${y}`;
    }

    setDateModalOpen(false);

    try {
      const token = localStorage.getItem("u_token");
      const response = await axios.post(
        `${Apiurl}admin/inventory/update`,
        {
          id: row.id,
          sku_code: row.sku_code,
          status: newStatus,
          pruning_date: pruningDate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status) {
        Swal.fire({
          title: "Success",
          text: `Status updated to ${newStatus}`,
          icon: "success",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
        dispatch(
          fetchAdminInventory({
            search: searchQuery,
            page: page + 1,
            limit: rowsPerPage,
            sort_by: sortBy.sort_by,
            sort_order: sortBy.order,
          })
        );
      } else {
        Swal.fire("Error", response.data?.message || "Failed to update status", "error");
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Network error while updating status.", "error");
    }
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "Active":
        return { bg: "#dcfce7", text: "#15803d", border: "#86efac" }; // Green
      case "Pruned":
        return { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" }; // Red
      case "Pruning":
        return { bg: "#fef9c3", text: "#a16207", border: "#fde047" }; // Yellow
      default:
        return { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" };
    }
  };


  return (
    <MainLayout title={'Inventory'}>

      <UploadProgressBar progress={uploadProgress} isVisible={loading} />

      <Box
        sx={{
          p: "12px 16px",
          bgcolor: "background.paper",
          background: "linear-gradient(to top left, #f5f8fa, #e9eef2)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          mb: 2,
          border: "1px solid #e0e6ed",
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search SKU Code..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: "20px", color: "#64748b" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: "white",
            width: "320px",
            "& .MuiOutlinedInput-root": {
              height: "32px",
              fontSize: "13px",
            },
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Master Switch Card */}
          <div className="bg-white p-1.5 rounded-[12px] shadow-sm flex items-center gap-2 shrink-0 border border-slate-200 pr-3 mr-2">
            <div className={`p-1.5 rounded-lg transition-colors duration-300 shrink-0 ${inventoryVisible ? 'bg-green-100' : 'bg-red-100'}`}>
              <Power className={`w-4 h-4 ${inventoryVisible ? 'text-green-600' : 'text-red-600'}`} />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none">User Menu</p>
              <button onClick={handleToggle} disabled={toggling}
                className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-300 ${inventoryVisible ? 'bg-green-500' : 'bg-slate-300'
                  } ${toggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${inventoryVisible ? 'translate-x-4' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>

          <input
            type="file"
            hidden
            id="inventory-file-input"
            onChange={handleFileUpload}
            accept=".xlsx, .xls"
          />
          <Button
            variant="contained"
            startIcon={<FileUpload />}
            onClick={() => document.getElementById("inventory-file-input")?.click()}
            disabled={loading}
            className="capitalize font-bold bg-slate-900 hover:bg-black text-white shadow-none transition-all"
            sx={{
              py: "2px",
              px: "14px",
              borderRadius: 12,
              fontSize: "12px",
              whiteSpace: "nowrap",
            }}
          >
            Upload Stock
          </Button>

          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={handleDownloadTemplate}
            className="capitalize font-bold border-slate-300 text-slate-700 hover:bg-slate-50 transition-all"
            sx={{
              py: "2px",
              px: "14px",
              borderRadius: 12,
              fontSize: "12px",
              whiteSpace: "nowrap",
            }}
          >
            Template
          </Button>

          <Button
            variant="contained"
            startIcon={<TableChartIcon />}
            onClick={handleDownloadExcel}
            sx={{
              textTransform: "none",
              bgcolor: "#2e7d32",
              py: "2px",
              px: "14px",
              borderRadius: 12,
              fontSize: "12px",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              boxShadow: "none",
              "&:hover": { bgcolor: "#1b5e20", boxShadow: "none" },
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "4px",
          border: "1px solid #1e293b",
          overflow: "hidden",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table size="small" stickyHeader aria-label="inventory table">

            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#04121f",
                    color: "#ffffff",
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    borderRight: "1px solid #1e293b",
                    borderBottom: "1px solid #1e293b",
                    position: "sticky", // Added
                    top: 0, // Added
                    zIndex: 11, // Higher z-index for top row
                  },
                }}
              >
                <TableCell rowSpan={2} align="center" sx={{ width: "80px" }}>
                  SL No.
                </TableCell>
                <TableCell rowSpan={2} sx={{ minWidth: "120px" }}>
                  SKU Code
                </TableCell>
                <TableCell rowSpan={2} sx={{ minWidth: "200px" }}>
                  Description
                </TableCell>
                <TableCell rowSpan={2} sx={{ minWidth: "100px" }}>
                  Preferences
                </TableCell>
                <TableCell rowSpan={2} align="center">
                  Indicator
                </TableCell>
                <TableCell rowSpan={2} align="center">
                  <TableSortLabel
                    active={sortBy.sort_by === "stockAvailable"}
                    direction={
                      sortBy.sort_by === "stockAvailable"
                        ? sortBy.order
                        : "desc"
                    }
                    onClick={() => handleSort("stockAvailable")}
                    sx={{
                      color: "white !important",
                      "&.Mui-active": { color: "#38bdf8 !important" },
                      "& .MuiTableSortLabel-icon": {
                        color: "#38bdf8 !important",
                      },
                    }}
                  >
                    Available
                  </TableSortLabel>
                </TableCell>
                <TableCell rowSpan={2} align="center" sx={{ minWidth: "130px" }}>
                  Status
                </TableCell>
                <TableCell rowSpan={2} align="center" sx={{ minWidth: "130px" }}>
                  Pruning Date
                </TableCell>
                <TableCell rowSpan={2} align="center">

                  Updated On
                </TableCell>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{
                    bgcolor: "#0f172a !important",
                    borderBottom: "2px solid #38bdf8 !important",
                  }}
                >
                  {currentMonth}
                </TableCell>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{
                    bgcolor: "#1e293b !important",
                    borderBottom: "2px solid #94a3b8 !important",
                  }}
                >
                  {nextMonth}
                </TableCell>
              </TableRow>

              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#04121f",
                    color: "#94a3b8",
                    fontSize: "10px",
                    fontWeight: 800,
                    borderRight: "1px solid #1e293b",
                    position: "sticky",
                    top: "33.5px",
                    zIndex: 10,
                  },
                }}
              >
                {["WK1", "WK2", "WK3", "WK4", "WK1", "WK2", "WK3", "WK4"].map(
                  (wk, i) => (
                    <TableCell key={i} align="center">
                      {wk}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {getStatus === "pending" ? (
                <TableBodySkeleton cols={14} rows={10} />
              ) : inventory?.map((row, index) => (
                <TableRow
                  key={row?.sku_code || index}
                  hover
                  sx={{
                    "&:nth-of-type(even)": {
                      backgroundColor: "#fcfcfc",
                    },
                    "& td": {
                      fontSize: "12px",
                      borderRight: "1px solid #f1f5f9",
                      borderBottom: "1px solid #f1f5f9",
                      color: "#1e293b",
                      py: 1.2,
                    },
                    "&:hover": {
                      backgroundColor: "#f1f5f9 !important",
                    },
                  }}
                >

                  <TableCell
                    align="center"
                    sx={{ color: "#64748b", fontWeight: 500 }}
                  >
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a",
                      fontFamily: "monospace",
                    }}
                  >
                    <ClickableTableLink path="/admin/product" displayValue={row.sku_code} searchValue={row.sku_code} />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#475569",
                      width: "280px",
                      minWidth: "200px",
                      lineHeight: "1.4",
                      fontSize: "11px",
                      fontWeight: 500,
                      verticalAlign: "top",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      py: 1.5,
                    }}
                  >
                    {row?.product?.product_desc || "—"}
                  </TableCell>
                  <TableCell
                sx={{
                      fontWeight: 700,
                      color: "#0f172a",
                      fontFamily: "monospace",
                    }}
                  >
                    {row?.product?.preferences || "—"}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 1,
                        py: 0.25,
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: 800,
                        bgcolor: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      {row?.stockIndicator || "—"}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 800,
                      bgcolor: "#fff7ed",
                      color: "#c2410c",
                    }}
                  >
                    {formatStockValue(row?.stockAvailable)}
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const currentStatus = row?.status || "";
                      const styles = getStatusStyles(currentStatus);
                      return (
                        <Select
                          size="small"
                          displayEmpty
                          value={currentStatus}
                          onChange={(e) => handleStatusChange(row, e.target.value)}
                          sx={{
                            fontSize: "11px",
                            fontWeight: 800,
                            height: "26px",
                            bgcolor: styles.bg,
                            color: `${styles.text} !important`,
                            border: `1px solid ${styles.border}`,
                            borderRadius: "6px",
                            "& .MuiSelect-select": {
                              py: "2px",
                              px: "10px",
                              display: "flex",
                              alignItems: "center",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            "& .MuiSvgIcon-root": {
                              color: styles.text,
                            },
                          }}
                        >
                          <MenuItem value=""  sx={{ fontSize: "12px", fontWeight: 700, color: "#64748b" }}>
                            Select
                          </MenuItem>
                          <MenuItem value="Active" sx={{ fontSize: "12px", fontWeight: 700, color: "#15803d" }}>
                            Active
                          </MenuItem>
                          <MenuItem value="Pruning" sx={{ fontSize: "12px", fontWeight: 700, color: "#b91c1c" }}>
                            Pruning
                          </MenuItem>
                          <MenuItem value="Pruned" sx={{ fontSize: "12px", fontWeight: 700, color: "#a16207" }}>
                            Pruned
                          </MenuItem>
                        </Select>
                      );
                    })()}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 800,
                      bgcolor: "#fff7ed",
                      color: "#c2410c",
                    }}
                  >
                    {row?.pruning_date ? row?.pruning_date : "—"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 800,
                      bgcolor: "#fff7ed",
                      color: "#c2410c",
                    }}
                  >
                    {new Date(row?.updated_at).toLocaleString('en-GB') || "—"}
                  </TableCell>

                  {[row?.cwk_1, row?.cwk_2, row?.cwk_3, row?.cwk_4].map(
                    (val, i) => (
                      <TableCell
                        key={`c-${i}`}
                        align="center"
                        sx={{
                          color: val > 0 ? "#15803d" : "#cbd5e1",
                          fontWeight: val > 0 ? 700 : 400,
                        }}
                      >
                        {formatStockValue(val)}
                      </TableCell>
                    )
                  )}

                  {[row?.nwk_1, row?.nwk_2, row?.nwk_3, row?.nwk_4].map(
                    (val, i) => (
                      <TableCell
                        key={`n-${i}`}
                        align="center"
                        sx={{
                          color: val > 0 ? "#15803d" : "#cbd5e1",
                          bgcolor: "#f8fafc",
                          fontWeight: val > 0 ? 700 : 400,
                        }}
                      >
                        {formatStockValue(val)}
                      </TableCell>
                    )
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 50, 100, 500, totalCount]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: "2px solid #04121f",
            bgcolor: "#ffffff",
            "& .MuiTablePagination-toolbar": { minHeight: "45px" },
          }}
        />

        <Dialog
          open={dateModalOpen}
          onClose={() => setDateModalOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              padding: "8px",
              width: "100%",
              maxWidth: "380px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <DialogTitle sx={{ p: 2, pb: 1 }}>
            {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  bgcolor: "#f1f5f9",
                  p: 1,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: "20px", color: "#0f172a" }} />
              </Box>
              <Box sx={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>
                Select Pruning Date
              </Box>
            </Box> */}
          </DialogTitle>

          <DialogContent sx={{ p: 2, py: 1 }}>
            <Box sx={{ fontSize: "12px", color: "#64748b", mb: 2, fontWeight: 500 }}>
              Set target pruning date for SKU:{" "}
              <span style={{ fontWeight: 700, color: "#0f172a" }}>
                {selectedRow?.sku_code}
              </span>
            </Box>

            <TextField
              type="date"
              fullWidth
              size="small"
              value={pruningDateVal}
              onChange={(e) => setPruningDateVal(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  bgcolor: "#f8fafc",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0f172a",
                  "& fieldset": {
                    borderColor: "#e2e8f0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#cbd5e1",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#0f172a",
                  },
                },
              }}
            />
          </DialogContent>

          <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setDateModalOpen(false)}
              variant="text"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                color: "#64748b",
                px: 2,
                "&:hover": { bgcolor: "#f1f5f9" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleStatusChange(selectedRow, pendingStatus, pruningDateVal)}
              variant="contained"
              disableElevation
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                bgcolor: "#0f172a",
                color: "#ffffff",
                px: 2.5,
                "&:hover": { bgcolor: "#1e293b" },
              }}
            >
              Apply Date
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </MainLayout>
  );
}
